import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useAppContext } from '../context/AppContext';
import { usePanZoom } from '../hooks/usePanZoom';
import TileImage from './TileImage';

const SNAP = 10;
const CANVAS_SIZE = 1000;

export default function Canvas() {
  const { mode, tiles, selection, setSelection, setIsPurchaseModalOpen, setIsAuthModalOpen, user, focusTileId, setFocusTileId } = useAppContext();
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ w: window.innerWidth, h: window.innerHeight });
  const [highlightId, setHighlightId] = useState(null);
  const [dragStart, setDragStart] = useState(null);
  const [currentDrag, setCurrentDrag] = useState(null);
  const touchRef = useRef({ type: null, lastX: 0, lastY: 0, lastDist: 0 });

  useEffect(() => {
    const update = () => setContainerSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const { transform, setTransform, handleWheel, handleMouseDown: panDown, handleMouseMove: panMove, handleMouseUp: panUp, flyToTile } = usePanZoom(containerSize);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => { e.preventDefault(); handleWheel(e); };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [handleWheel]);

  useEffect(() => {
    if (!focusTileId) return;
    const tile = tiles.find(t => t.id === focusTileId);
    if (tile) {
      flyToTile(tile, containerSize);
      setHighlightId(focusTileId);
      setTimeout(() => { setHighlightId(null); setFocusTileId(null); }, 3000);
    }
  }, [focusTileId, tiles, containerSize, flyToTile, setFocusTileId]);

  const getCanvasCoords = useCallback((clientX, clientY) => {
    const rect = containerRef.current.getBoundingClientRect();
    return {
      x: (clientX - rect.left - transform.x) / transform.scale,
      y: (clientY - rect.top - transform.y) / transform.scale,
    };
  }, [transform]);

  const handleMouseDown = (e) => {
    if (mode === 'pan') { panDown(e); return; }
    const { x, y } = getCanvasCoords(e.clientX, e.clientY);
    const sx = Math.floor(x / SNAP) * SNAP, sy = Math.floor(y / SNAP) * SNAP;
    if (sx >= 0 && sx < CANVAS_SIZE && sy >= 0 && sy < CANVAS_SIZE) {
      setDragStart({ x: sx, y: sy });
      setCurrentDrag({ x: sx, y: sy });
    }
  };

  const handleMouseMove = (e) => {
    if (mode === 'pan') { panMove(e); return; }
    if (!dragStart) return;
    const { x, y } = getCanvasCoords(e.clientX, e.clientY);
    setCurrentDrag({
      x: Math.max(0, Math.min(Math.floor(x / SNAP) * SNAP, CANVAS_SIZE - SNAP)),
      y: Math.max(0, Math.min(Math.floor(y / SNAP) * SNAP, CANVAS_SIZE - SNAP)),
    });
  };

  const handleMouseUp = () => {
    if (mode === 'pan') { panUp(); return; }
    if (dragStart && currentDrag) {
      const x = Math.min(dragStart.x, currentDrag.x), y = Math.min(dragStart.y, currentDrag.y);
      const w = Math.abs(currentDrag.x - dragStart.x) + SNAP, h = Math.abs(currentDrag.y - dragStart.y) + SNAP;
      setSelection({ x, y, w, h });
      if (!user) setIsAuthModalOpen(true);
      else setIsPurchaseModalOpen(true);
      setDragStart(null); setCurrentDrag(null);
    }
  };

  const handleTouchStart = useCallback((e) => {
    e.preventDefault();
    if (e.touches.length === 1) {
      const t = e.touches[0];
      if (mode === 'pan') {
        touchRef.current = { type: 'pan', lastX: t.clientX, lastY: t.clientY, lastDist: 0 };
      } else {
        const { x, y } = getCanvasCoords(t.clientX, t.clientY);
        const sx = Math.floor(x / SNAP) * SNAP, sy = Math.floor(y / SNAP) * SNAP;
        if (sx >= 0 && sx < CANVAS_SIZE && sy >= 0 && sy < CANVAS_SIZE) {
          touchRef.current = { type: 'select' };
          setDragStart({ x: sx, y: sy });
          setCurrentDrag({ x: sx, y: sy });
        }
      }
    } else if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      touchRef.current = {
        type: 'pinch', lastDist: Math.hypot(dx, dy),
        midX: (e.touches[0].clientX + e.touches[1].clientX) / 2,
        midY: (e.touches[0].clientY + e.touches[1].clientY) / 2,
      };
    }
  }, [mode, getCanvasCoords]);

  const handleTouchMove = useCallback((e) => {
    e.preventDefault();
    if (e.touches.length === 1 && touchRef.current.type === 'pan') {
      const t = e.touches[0];
      const dx = t.clientX - touchRef.current.lastX;
      const dy = t.clientY - touchRef.current.lastY;
      touchRef.current.lastX = t.clientX;
      touchRef.current.lastY = t.clientY;
      setTransform(prev => ({ ...prev, x: prev.x + dx, y: prev.y + dy }));
    } else if (e.touches.length === 1 && touchRef.current.type === 'select') {
      const t = e.touches[0];
      const { x, y } = getCanvasCoords(t.clientX, t.clientY);
      setCurrentDrag({
        x: Math.max(0, Math.min(Math.floor(x / SNAP) * SNAP, CANVAS_SIZE - SNAP)),
        y: Math.max(0, Math.min(Math.floor(y / SNAP) * SNAP, CANVAS_SIZE - SNAP)),
      });
    } else if (e.touches.length === 2 && touchRef.current.type === 'pinch') {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      const rect = containerRef.current.getBoundingClientRect();
      const localMidX = midX - rect.left;
      const localMidY = midY - rect.top;
      const zoomFactor = dist / touchRef.current.lastDist;
      touchRef.current.lastDist = dist;
      setTransform(prev => {
        const newScale = Math.min(Math.max(0.3, prev.scale * zoomFactor), 10);
        const ratio = newScale / prev.scale;
        return { scale: newScale, x: localMidX - ratio * (localMidX - prev.x), y: localMidY - ratio * (localMidY - prev.y) };
      });
    }
  }, [getCanvasCoords, setTransform]);

  const handleTouchEnd = useCallback((e) => {
    e.preventDefault();
    if (touchRef.current.type === 'select' && dragStart && currentDrag) {
      const x = Math.min(dragStart.x, currentDrag.x), y = Math.min(dragStart.y, currentDrag.y);
      const w = Math.abs(currentDrag.x - dragStart.x) + SNAP, h = Math.abs(currentDrag.y - dragStart.y) + SNAP;
      setSelection({ x, y, w, h });
      if (!user) setIsAuthModalOpen(true);
      else setIsPurchaseModalOpen(true);
      setDragStart(null); setCurrentDrag(null);
    }
    touchRef.current = { type: null };
  }, [dragStart, currentDrag, user, setSelection, setIsAuthModalOpen, setIsPurchaseModalOpen]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('touchstart', handleTouchStart, { passive: false });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    el.addEventListener('touchend', handleTouchEnd, { passive: false });
    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  const handleTileClick = (e, tile) => {
    if (mode !== 'pan') return;
    e.stopPropagation();
    if (tile.link) window.open(tile.link, '_blank', 'noopener,noreferrer');
  };

  let renderSelection = selection;
  if (dragStart && currentDrag) {
    renderSelection = {
      x: Math.min(dragStart.x, currentDrag.x),
      y: Math.min(dragStart.y, currentDrag.y),
      w: Math.abs(currentDrag.x - dragStart.x) + SNAP,
      h: Math.abs(currentDrag.y - dragStart.y) + SNAP,
    };
  }

  return (
    <div
      ref={containerRef}
      className={`absolute inset-0 overflow-hidden ${mode === 'pan' ? 'cursor-grab active:cursor-grabbing' : 'cursor-crosshair'}`}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      style={{ touchAction: 'none', background: '#050010' }}
    >
      <div
        className="absolute origin-top-left"
        style={{
          width: CANVAS_SIZE, height: CANVAS_SIZE,
          transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
          background: '#0a0010',
          willChange: 'transform',
        }}
      >
        {/* Grid lines */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundSize: '10px 10px',
          backgroundImage: `linear-gradient(to right, rgba(255,0,255,0.07) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,0,255,0.07) 1px, transparent 1px)`,
        }} />

        {/* Radial glow */}
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 50% 50%, rgba(255,0,200,0.05) 0%, transparent 70%)' }} />

        {/* Tiles */}
        {tiles.map(tile => {
          const isHighlighted = highlightId === tile.id;
          const minDim = Math.min(tile.w, tile.h);
          const fontSize = Math.max(6, Math.floor(minDim * 0.35));

          return (
            <div
              key={tile.id}
              className="absolute group"
              style={{
                left: tile.x, top: tile.y, width: tile.w, height: tile.h,
                zIndex: isHighlighted ? 50 : tile.hasGlow ? 10 : 1,
                cursor: mode === 'pan' ? 'pointer' : 'crosshair',
                outline: isHighlighted
                  ? '3px solid #fff'
                  : tile.hasGlow
                    ? '2px solid rgba(255,0,200,0.95)'
                    : 'none',
                outlineOffset: '0px',
                filter: isHighlighted
                  ? 'drop-shadow(0 0 8px rgba(255,255,0,0.9)) drop-shadow(0 0 20px rgba(255,255,0,0.6))'
                  : tile.hasGlow
                    ? 'drop-shadow(0 0 6px rgba(255,0,200,1)) drop-shadow(0 0 14px rgba(255,0,200,0.7)) drop-shadow(0 0 28px rgba(255,0,200,0.4))'
                    : 'none',
                overflow: 'hidden',
              }}
              onClick={(e) => handleTileClick(e, tile)}
            >
              {tile.solidColor ? (
                <div className="w-full h-full flex items-center justify-center" style={{ background: tile.solidColor }}>
                  {tile.blockText && (
                    <span
                      className="font-black text-center px-0.5 leading-tight"
                      style={{ color: tile.blockTextColor || '#fff', fontSize: `${fontSize}px`, wordBreak: 'break-word' }}
                    >
                      {tile.blockText}
                    </span>
                  )}
                </div>
              ) : tile.image ? (
                <TileImage
                  src={tile.image}
                  alt={tile.title}
                  className="w-full h-full object-cover pointer-events-none block"
                  style={{ display: 'block' }}
                />
              ) : (
                <div className="w-full h-full" style={{ background: '#1a0030' }} />
              )}

              {/* Tooltip — desktop only */}
              <div
                className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 w-max max-w-[160px] p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 hidden md:block"
                style={{ background: 'rgba(5,0,16,0.98)', border: '1px solid rgba(255,0,200,0.4)' }}
              >
                <div className="font-black text-sm truncate" style={{ color: '#ff00cc' }}>{tile.title}</div>
                <div className="text-gray-400 truncate text-[10px] mt-0.5">{tile.link}</div>
              </div>
            </div>
          );
        })}

        {/* Selection overlay */}
        {renderSelection && (
          <div className="absolute z-20 pointer-events-none" style={{
            left: renderSelection.x, top: renderSelection.y,
            width: renderSelection.w, height: renderSelection.h,
            border: '2px solid #ff00cc',
            background: 'rgba(255,0,200,0.15)',
            boxShadow: '0 0 18px rgba(255,0,200,0.5)',
          }} />
        )}
      </div>
    </div>
  );
}